import {AfterViewInit, Component, inject, input, OnDestroy, ViewChild} from '@angular/core';
import {
  AbstractControl,
  AsyncValidator,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  NG_ASYNC_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  TouchedChangeEvent,
  ValidationErrors,
  Validators
} from '@angular/forms';
import {MatError, MatFormField, MatSuffix} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {combineLatestWith, filter, interval, Observable, of, Subscription} from 'rxjs';
import {MatOption, MatSelect} from '@angular/material/select';
import {ReportingType, VehicleModel} from '../../../../../shared/models/vehicle.model';
import {RowWithToggleComponent} from '../../row-with-toggle/row-with-toggle.component';
import {NgxColorsModule} from 'ngx-colors';
import {VehiclesService} from '../../../../../data/vehicles/vehicles.service';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {distinctUntilChanged, map, take} from 'rxjs/operators';
import {toObservable} from '@angular/core/rxjs-interop';
import {ActivatedRoute} from '@angular/router';

@Component({
  selector: 'app-vehicle-basic-information-edit',
  standalone: true,
  imports: [
    FormsModule,
    MatError,
    MatFormField,
    MatInput,
    ReactiveFormsModule,
    MatSelect,
    MatOption,
    RowWithToggleComponent,
    NgxColorsModule,
    MatSuffix,
    MatProgressSpinner
  ],
  templateUrl: './vehicle-basic-information-edit.component.html',
  styleUrls: ['./vehicle-basic-information-edit.component.scss', '../../../settings-fields.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: VehicleBasicInformationEditComponent
    },
    {
      provide: NG_ASYNC_VALIDATORS,
      multi: true,
      useExisting: VehicleBasicInformationEditComponent
    }
  ]
})
export class VehicleBasicInformationEditComponent implements ControlValueAccessor, OnDestroy, AsyncValidator, AfterViewInit {

  private readonly fb = inject(FormBuilder);
  private readonly vehiclesService = inject(VehiclesService);
  private readonly activatedRoute = inject(ActivatedRoute);

  formGroup: FormGroup;

  vehicleId?: number;

  isImported = input(false);
  showJustRequiredFields = input(false);
  @ViewChild('statusSelect', {static: true}) statusSelect: MatSelect;
  protected readonly ReportingType = ReportingType;
  private readonly subscriptions: Subscription[] = [];


  constructor() {
    this.formGroup = this.fb.group({
      name: this.fb.control(null, {
        validators: Validators.required,
        updateOn: 'blur',
        asyncValidators: this.validateValueUniqueness('name', 'vehicleNameTaken'),
      }),
      label: this.fb.control(null, {
        updateOn: 'blur',
        asyncValidators: this.validateValueUniqueness('label', 'vehicleLabelTaken'),
      }),
      publicAlias: this.fb.control(null, {
        updateOn: 'blur',
        asyncValidators: this.validateValueUniqueness('publicAlias', 'vehiclePublicAliasTaken'),
      }),
      licensePlate: this.fb.control(null, {
        updateOn: 'blur',
        asyncValidators: this.validateValueUniqueness('licensePlate', 'licensePlateNumberTaken'),
      }),
      active: this.fb.control(true),
      outOfServiceNote: this.fb.control(null, {
        validators: (control) => {
          if (this.showJustRequiredFields() === true) {
            return null;
          }
          const {active} = this.formGroup?.value || {};
          const outOfServiceNote = control.value;
          if (active || outOfServiceNote) {
            return null;
          }
          return {required: true};
        }
      }),
      reportingType: this.fb.control(ReportingType.DISTANCE),
      mapColorOverride: this.fb.control(false),
      mapColor: this.fb.control(null, {
        validators: (control) => {
          if (this.showJustRequiredFields() === true) {
            return null;
          }
          const {mapColorOverride} = this.formGroup?.value || {};
          const mapColor = control.value;
          if (!mapColorOverride || mapColor) {
            return null;
          }
          return {required: true};
        }
      }),
      applyColorOverrideOnMobile: this.fb.control(false),
    }, {
      validators: (form) => {
        if (this.showJustRequiredFields() === true) {
          return null;
        }
        const {value} = form;
        let valid = true;
        const validatorErrors = {};
        if (value.mapColorOverride && !value.mapColor) {
          valid = false;
          validatorErrors['mapColorMissing'] = true;
        }
        if (!value.active && !value.outOfServiceNote) {
          valid = false;
          validatorErrors['outOfServiceNoteMissing'] = true;
        }
        if (valid) {
          return null;
        } else {
          return validatorErrors;
        }
      }
    });

    this.subscriptions.push(
      toObservable(this.isImported).pipe(
        combineLatestWith(toObservable(this.showJustRequiredFields)),
        distinctUntilChanged())
        .subscribe(([isImported, showJustRequiredFields]) => {
          const {name, label, licensePlate, active, mapColorOverride} = this.formGroup.controls;
          if (isImported || showJustRequiredFields) {
            label.disable({emitEvent: false});
            licensePlate.disable({emitEvent: false});
          } else {
            label.enable({emitEvent: false});
            licensePlate.enable({emitEvent: false});
          }

          if (isImported) {
            name.disable({emitEvent: false});
          } else {
            name.enable({emitEvent: false});
          }

          if (showJustRequiredFields) {
            active.disable({emitEvent: false});
            mapColorOverride.disable({emitEvent: false});
          } else {
            active.enable({emitEvent: true});
            mapColorOverride.enable({emitEvent: true});
          }

          this.formGroup.updateValueAndValidity();
        })
    );

    this.subscriptions.push(
      this.formGroup.controls.mapColorOverride.valueChanges.subscribe(override => {
        const {mapColor, applyColorOverrideOnMobile} = this.formGroup.controls;
        if (override) {
          mapColor.enable({emitEvent: false});
          applyColorOverrideOnMobile.enable({emitEvent: false});
        } else {
          mapColor.disable({emitEvent: false});
          applyColorOverrideOnMobile.disable({emitEvent: false});
        }
      })
    );

    this.subscriptions.push(
      this.formGroup.controls.active.valueChanges.subscribe(active => {
        const {outOfServiceNote} = this.formGroup.controls;
        if (!active) {
          outOfServiceNote.enable({emitEvent: false});
          outOfServiceNote.markAsTouched({emitEvent: true});
        } else {
          outOfServiceNote.disable({emitEvent: false});
        }
      })
    );

    this.subscriptions.push(
      this.formGroup.events
        .pipe(
          filter(e => (e instanceof TouchedChangeEvent) && e.touched)
        )
        .subscribe(() => this.onTouchedListener())
    );

    this.subscriptions.push(
      this.formGroup.valueChanges
        .subscribe(value => {
          if (!value.mapColorOverride) {
            value.mapColor = null;
            value.applyColorOverrideOnMobile = null;
          }
          delete value.mapColorOverride;
          if (value.active) {
            value.outOfServiceNote = null;
          }
          this.onChangeListener(value);
        })
    );
  }

  onChangeListener = _ => {
  };

  onTouchedListener = () => {
  };

  onValidatorChangeListener = () => {
  };

  ngAfterViewInit() {
    this.activatedRoute.queryParams.subscribe((params) => {
      if (!!params.editStatus) {
        // delay opening the select box to ensure it's visible
        setTimeout(() => {
          this.statusSelect.open();
        }, 500);
      }
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  writeValue(value: any): void {
    if (!value) {
      return;
    }

    this.vehicleId = value.id;
    value.mapColorOverride = !!value.mapColor;

    const {
      name,
      label,
      publicAlias,
      licensePlate,
      active,
      outOfServiceNote,
      reportingType,
      mapColor,
      applyColorOverrideOnMobile,
    } = value;
    this.formGroup.setValue({
      name: name || null,
      label: label || null,
      publicAlias: publicAlias || null,
      licensePlate: licensePlate || null,
      active: active || false,
      outOfServiceNote: outOfServiceNote || null,
      reportingType: reportingType || ReportingType.DISTANCE,
      mapColorOverride: !!mapColor,
      mapColor: mapColor || null,
      applyColorOverrideOnMobile: applyColorOverrideOnMobile || null,
    }, {
      emitEvent: false
    });
  }

  registerOnChange(fn: any): void {
    this.onChangeListener = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedListener = fn;
  }

  registerOnValidatorChange(fn: () => void) {
    this.onValidatorChangeListener = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled === this.formGroup.disabled) {
      return;
    }

    if (isDisabled) {
      this.formGroup.disable({emitEvent: false});
    } else {
      this.formGroup.enable({emitEvent: false});
    }
  }

  validate(_: FormControl): Observable<ValidationErrors | null> {
    return interval(100)
      .pipe(
        map(() => this.formGroup.status),
        filter(status => (status === 'VALID' || status === 'INVALID')),
        take(1),
        map(status => status === 'INVALID' ? {_control: {error: true}} : null),
      );
  }

  private validateValueUniqueness(fieldName: string, resultFieldName: string) {
    return (control: AbstractControl) => {
      const {value, pristine} = control;
      if (!value || pristine) {
        return of(null);
      }
      return this.vehiclesService.checkVehicleDataUniqueness({
        id: this.vehicleId,
        [fieldName]: value
      } as VehicleModel).pipe(
        map(result => result[resultFieldName] ? {notUnique: true} as ValidationErrors : null),
      );
    };
  }

}
